"use client"
import {useEffect, useRef, useState} from "react";

export default function History({children}) {
    const scrollRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)

    const [thumbWidth, setThumbWidth] = useState(0)
    const [thumbLeft, setThumbLeft] = useState(0)

    const [isDragging, setIsDragging] = useState(false)

    const updateScrollbar = () => {
        const scroll = scrollRef.current
        const track = trackRef.current

        if (!scroll || !track) return

        const {scrollWidth, clientWidth, scrollLeft} = scroll
        const trackWidth = track.clientWidth

        const width = Math.max(
            (clientWidth / scrollWidth) * trackWidth,
            40
        )

        const maxScroll = scrollWidth - clientWidth
        const maxThumbLeft = trackWidth - width

        const left =
            maxScroll > 0
                ? (scrollLeft / maxScroll) * maxThumbLeft
                : 0

        setThumbWidth(width)
        setThumbLeft(left)
    }

    useEffect(() => {
        updateScrollbar()

        const scroll = scrollRef.current

        if (!scroll) return

        scroll.addEventListener("scroll", updateScrollbar)
        window.addEventListener("resize", updateScrollbar)

        return () => {
            scroll.removeEventListener("scroll", updateScrollbar)
            window.removeEventListener("resize", updateScrollbar)
        }
    }, [])

    const handleTrackClick = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        const scroll = scrollRef.current
        const track = trackRef.current

        if (!scroll || !track) return

        const rect = track.getBoundingClientRect()
        const clickPosition = event.clientX - rect.left

        const maxScroll = scroll.scrollWidth - scroll.clientWidth
        const maxThumbLeft = track.clientWidth - thumbWidth

        const newScrollLeft =
            (clickPosition / maxThumbLeft) * maxScroll

        scroll.scrollTo({
            left: newScrollLeft,
            behavior: "smooth",
        })
    }

    const handleThumbDrag = (
        event: React.PointerEvent<HTMLDivElement>
    ) => {
        const scroll = scrollRef.current
        const track = trackRef.current

        if (!scroll || !track) return

        setIsDragging(true)

        event.currentTarget.setPointerCapture(event.pointerId)

        const startX = event.clientX
        const startScrollLeft = scroll.scrollLeft

        const maxScroll = scroll.scrollWidth - scroll.clientWidth
        const maxThumbLeft = track.clientWidth - thumbWidth

        const handleMove = (moveEvent: PointerEvent) => {
            const deltaX = moveEvent.clientX - startX

            const scrollDelta =
                (deltaX / maxThumbLeft) * maxScroll

            scroll.scrollLeft = startScrollLeft + scrollDelta
        }

        const handleUp = () => {
            setIsDragging(false)
            window.removeEventListener("pointermove", handleMove)
            window.removeEventListener("pointerup", handleUp)
        }

        window.addEventListener("pointermove", handleMove)
        window.addEventListener("pointerup", handleUp)
    }

    const handleContentPointerDown = (
        event: React.PointerEvent<HTMLDivElement>
    ) => {
        const scroll = scrollRef.current

        if (!scroll) return

        // Only use custom drag scrolling for mouse/pen.
        // Touch devices keep native touch scrolling.
        if (event.pointerType === "touch") return

        setIsDragging(true)

        const startX = event.clientX
        const startScrollLeft = scroll.scrollLeft

        const handleMove = (moveEvent: PointerEvent) => {
            const deltaX = moveEvent.clientX - startX

            scroll.scrollLeft =
                startScrollLeft - deltaX
        };

        const handleUp = () => {
            setIsDragging(false)

            window.removeEventListener(
                "pointermove",
                handleMove
            )

            window.removeEventListener(
                "pointerup",
                handleUp
            )
        }

        window.addEventListener(
            "pointermove",
            handleMove
        )

        window.addEventListener(
            "pointerup",
            handleUp
        )
    }

    return <>
        <div
            ref={scrollRef}
            className={`overflow-x-scroll no-scrollbar cursor-grab ${isDragging ? 'snap-none cursor-grabbing' : 'snap-x snap-mandatory'}`}
            onPointerDown={handleContentPointerDown}
        >
            <div className="flex gap-9 w-max">
                {children}
            </div>
        </div>
        <div
            ref={trackRef}
            onClick={handleTrackClick}
            className="relative mt-6 h-1 border-b w-full cursor-pointer bg-transparent border-blue-200"
        >
            <div
                onPointerDown={handleThumbDrag}
                style={{
                    width: `${thumbWidth}px`,
                    transform: `translateX(${thumbLeft}px)`,
                }}
                className="absolute left-0 top-0 h-1 cursor-grab bg-primary active:cursor-grabbing"
            ></div>
        </div>
    </>
}